// Typed client for the course engine (docs/courses/handoff-backend.md §2, §3, §6). Pure; the hook is in useCoursesApi.ts.
import type {
  CheckAnswer,
  CourseKey,
  CourseOutline,
  CourseProgress,
  CourseRequirement,
  CourseStepView,
  FinalTestResult,
  FinalTestStart,
  StepSubmitResult,
} from "./types.js"

type FetchInit = Parameters<typeof fetch>[1]

export type RequestFn = (path: string, init?: FetchInit) => Promise<Response>

export class CoursesApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, body: unknown) {
    const message =
      body && typeof body === "object" && typeof (body as { message?: unknown }).message === "string"
        ? (body as { message: string }).message
        : `Pedido falhou (${status})`
    super(message)
    this.status = status
    this.body = body
  }
}

export interface StepSubmission {
  attemptId?: string
  answer?: CheckAnswer
  msSpent: number
}

export interface CoursesApi {
  getOutline(courseKey: CourseKey): Promise<CourseOutline>
  getProgress(courseKey: CourseKey): Promise<CourseProgress>
  getStep(courseKey: CourseKey, stepId: string): Promise<CourseStepView>
  submitStep(courseKey: CourseKey, stepId: string, submission: StepSubmission): Promise<StepSubmitResult>
  startFinalTest(courseKey: CourseKey): Promise<FinalTestStart>
  answerFinalTest(
    courseKey: CourseKey,
    attemptId: string,
    payload: { stepId: string; answer: CheckAnswer; msSpent: number },
  ): Promise<void>
  finishFinalTest(courseKey: CourseKey, attemptId: string): Promise<FinalTestResult>
  getRequirements(animalId: string): Promise<CourseRequirement>
  dismissAdopterPrompt(): Promise<void>
}

const JSON_HEADERS = { "Content-Type": "application/json" }

export function createCoursesApi(request: RequestFn): CoursesApi {
  async function call<T>(path: string, init?: FetchInit): Promise<T> {
    const res = await request(path, init)
    const body = await res.json().catch(() => ({}))
    if (!res.ok) throw new CoursesApiError(res.status, body)
    return body as T
  }

  function post<T>(path: string, payload?: unknown): Promise<T> {
    return call<T>(path, {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify(payload ?? {}),
    })
  }

  const base = (courseKey: CourseKey) => `/api/courses/${encodeURIComponent(courseKey)}`

  return {
    getOutline: (courseKey) => call(base(courseKey)),
    getProgress: (courseKey) => call(`${base(courseKey)}/progress`),
    getStep: (courseKey, stepId) => call(`${base(courseKey)}/steps/${encodeURIComponent(stepId)}`),
    submitStep: (courseKey, stepId, submission) =>
      post(`${base(courseKey)}/steps/${encodeURIComponent(stepId)}/submit`, submission),
    startFinalTest: (courseKey) => post(`${base(courseKey)}/final-test/start`),
    answerFinalTest: async (courseKey, attemptId, payload) => {
      await post(`${base(courseKey)}/final-test/${encodeURIComponent(attemptId)}/answer`, payload)
    },
    finishFinalTest: (courseKey, attemptId) =>
      post(`${base(courseKey)}/final-test/${encodeURIComponent(attemptId)}/finish`),
    getRequirements: (animalId) => call(`/api/courses/requirements?animalId=${encodeURIComponent(animalId)}`),
    dismissAdopterPrompt: async () => {
      await post("/api/users/me/adopter-course-prompt/dismiss")
    },
  }
}
